import { useSelector } from "react-redux";
import { UserRole, UserType } from "../_statics/UserStatics";
import { useMemo } from "react";
import { OrganizationType } from "../_statics/OrganizationType";

const useUserAuthentication = () => {
    const user = useSelector((state) => state.authentication.user);

    const isSuperUser = useMemo(
        () => user && (user.userRole === UserRole.SUPER_USER || user.userRole === UserRole.TECHNICAL_USER),
        [user]
    );
    const isAdminUser = useMemo(() => user && user.userRole === UserRole.ADMINISTRATOR, [user]);

    const isCustomer = useMemo(() => user && user.platformUserType === UserType.CUSTOMER, [user]);
    const isTaxConsultant = useMemo(() => user && user.platformUserType === UserType.TAXCONSULTANT, [user]);

    const isInAgency = useMemo(() => user && user.inAgency, [user]);

    // Kanzleiadmin is derived from the existing Hauptansprechpartner (mainContact) field, not a separate
    // stored attribute: a user is the Kanzleiadmin iff they are the main contact of their selected TAX_AGENCY org.
    const isKanzleiadmin = useMemo(
        () =>
            !!user &&
            !!user.selectedOrganization &&
            user.selectedOrganization.organizationType === OrganizationType.TAX_AGENCY &&
            !!user.externalUUID &&
            user.externalUUID === user.selectedOrganization.mainContactExternalUUID,
        [user]
    );

    // Benutzerverwaltung access: the Kanzleiadmin, or a Super User acting on a selected Steuerkanzlei org
    // (support/admin override, so support can manage employees without a Kanzleiadmin transfer, #41913).
    const canManageKanzleiEmployees = useMemo(
        () =>
            !!user &&
            !!user.selectedOrganization &&
            user.selectedOrganization.organizationType === OrganizationType.TAX_AGENCY &&
            (isKanzleiadmin || isSuperUser),
        [user, isKanzleiadmin, isSuperUser]
    );

    // #42598: a Kanzleiadmin's elevated rights only apply inside his own Kanzlei, never on the Mandant side.
    const isRestrictedTaxAdvisorContext = useMemo(
        () =>
            !!isTaxConsultant &&
            !!user.selectedOrganization &&
            user.selectedOrganization.organizationType !== OrganizationType.TAX_AGENCY,
        [isTaxConsultant, user]
    );

    // #42598: "Darf Zahlungsart anpassen" Super-User override. Fails closed while user is still loading.
    const mayManagePaymentMethod = useMemo(
        () => !!user && (!isRestrictedTaxAdvisorContext || !!user.allowedToManagePaymentMethod),
        [isRestrictedTaxAdvisorContext, user]
    );

    const isShopifyAppCustomer = useMemo(
        () =>
            user &&
            user.selectedOrganization &&
            user.selectedOrganization.organizationType === OrganizationType.SHOPIFY_APPSTORE,
        [user]
    );

    const selectedOrganization = useMemo(() => {
        if (user) {
            return user.selectedOrganization;
        } else {
            return null;
        }
    }, [user]);

    const selectedOrganizationUUID = useMemo(() => {
        if (selectedOrganization) {
            return selectedOrganization.externalUUID;
        } else {
            return null;
        }
    }, [selectedOrganization]);

    return {
        isSuperUser,
        isAdminUser,

        isCustomer,
        isTaxConsultant,

        isInAgency,
        isKanzleiadmin,
        canManageKanzleiEmployees,
        isRestrictedTaxAdvisorContext,
        mayManagePaymentMethod,
        isShopifyAppCustomer,

        selectedOrganizationUUID,
        selectedOrganization,
    };
};

export default useUserAuthentication;
